<template>
    <modal-jetstream :show="show" :max-width="'3xl'" @close="$emit('close')">
        <div class="rounded-md shadow overflow-x-auto m-2">
            <div
                class="max-w-3xl mb-5 mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                <div>
                    <h1 class="text-2xl font-bold">Enhanced Screening Results</h1>
                </div>
            </div>
            <div class="flex flex-col">
                <div class="overflow-x-auto">
                    <div class="align-middle inline-block min-w-full">
                        <div class="shadow overflow-hidden">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-rent-risk-blue-400 text-white">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                        Title
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(addon, index) in (application?.completed_addons || [])" :key="index" class="bg-white border-b hover:bg-gray-100"
                                >
                                    <td class="px-6 whitespace-nowrap text-sm text-gray-500">
                                        <span v-if="addon.type === 'bank_income'">
                                            Bank Income Verification
                                        </span>
                                        <span v-else-if="addon.type === 'payroll'">
                                            Employment Verification
                                        </span>
                                        <span v-else>
                                            {{ addon.addon.title }}
                                        </span>

                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span v-if="addon.type === 'idv'">
                                            {{ (application?.completed_addons_ids || []).includes(addon.addon_id) ? 'Verified' : 'Pending' }}
                                        </span>
                                        <span v-else>
                                            {{ (application?.completed_addons_ids || []).includes(addon.addon_id) ? 'Complete' : 'Pending' }}
                                        </span>
                                    </td>
                                    <td class="px-6 whitespace-nowrap text-sm text-gray-500">
                                        <a v-if="addon.type === 'bank_income'" target="_blank" :href="route('user-bank-income.load', application?.id)" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">View</a>
                                        <a v-if="addon.type === 'payroll'" target="_blank" :href="route('user-payroll-income.load', application?.id)" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">View</a>
                                        <a v-if="addon.type === 'assets'" target="_blank" :href="route('user-assets.view-report', addon.asset_report_token)" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">View</a>
                                    </td>
                                </tr>
                                <template v-for="(addon, index) in (application?.required_addons || [])" :key="index"
                                >
                                    <tr v-if="!(application?.completed_addons_ids || []).includes(addon.addon_id) && !locallyRemovedAddonIds.includes(addon.id)" class="bg-white border-b hover:bg-gray-100">
                                        <td class="px-6 whitespace-nowrap text-sm text-gray-500">
                                            {{ addon.addon.title }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            Pending
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <!--
                                                RAC-10021: landlord-side cancel for a pending
                                                ESO. Some applicants (e.g. stay-at-home parents)
                                                can't satisfy income / employment verification —
                                                this lets the landlord remove the requirement
                                                so the applicant can complete the application.
                                                Only renders on pending rows; completed addons
                                                stay non-removable (data is already collected).
                                            -->
                                            <button
                                                type="button"
                                                @click="confirmRemove(addon)"
                                                :disabled="removingRequiredAddonId === addon.id"
                                                class="bg-white py-2 px-4 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 float-right disabled:opacity-50"
                                            >
                                                {{ removingRequiredAddonId === addon.id ? 'Removing…' : 'Remove' }}
                                            </button>
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </table>

                            <!-- Total Charged Amount -->
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-rent-risk-blue-400 text-white">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                        Payment Summary
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(payment, paymentIndex) in (application?.addon_payments || [])" :key="paymentIndex" class="bg-white border-b hover:bg-gray-100"
                                    >
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <template v-if="parseFloat(payment.amount) === 0">
                                                {{paymentIndex+1}}. $0.00 — Included with RiskGuard
                                            </template>
                                            <template v-else-if="payment.paid_by === 'applicant'">
                                                {{paymentIndex+1}}. ${{ payment.amount }}
                                                <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Paid by applicant</span>
                                            </template>
                                            <template v-else>
                                                {{paymentIndex+1}}. ${{ payment.amount }} (Payment Method: **** **** {{ payment?.payment_method_last_4 }})
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="col"
                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                                            Total: ${{ application?.addon_payments_sum_amount || 0 }}
                                        </th>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <button @click="$emit('close')" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
    </modal-jetstream>
</template>

<script>
import ModalJetstream from "@/Jetstream/Modal.vue";
import { router } from '@inertiajs/vue3';

export default {
    name: 'EnhancedScreeningResultsModal',
    components: {
        ModalJetstream,
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        application: {
            type: Object,
            required: true
        },
        // RAC-10021: needed to build the removeApplicationRequiredAddon
        // route. Parent (Properties/ApplicationDetails.vue) already has
        // it in scope as `property`.
        property: {
            type: Object,
            required: true,
        },
    },
    emits: ['close'],
    data() {
        return {
            // Tracks the in-flight removal so the button can disable + show
            // a spinner-style label while the Inertia request is open.
            removingRequiredAddonId: null,
            // Parents (V2 TenantScreeningTab, legacy Properties/Show) keep
            // the selected applicant as a local snapshot, so an Inertia
            // refresh after `back()` does NOT update the `application`
            // prop we receive. Without this list, removed rows linger
            // until the user closes and reopens the modal. Reset on
            // open so a fresh selection sees the current server truth.
            locallyRemovedAddonIds: [],
        };
    },
    computed: {
        hasApplication() {
            return this.application && typeof this.application === 'object'
        }
    },
    watch: {
        show(isOpen) {
            if (isOpen) {
                this.locallyRemovedAddonIds = [];
            }
        },
    },
    methods: {
        confirmRemove(requiredAddon) {
            const title = requiredAddon?.addon?.title ?? 'this Enhanced Screening Option';
            if (!window.confirm(`Cancel "${title}" for this applicant? They will no longer be required to complete it.`)) {
                return;
            }

            this.removingRequiredAddonId = requiredAddon.id;

            router.delete(
                route('properties.application.required_addons.remove', [
                    this.property.id,
                    this.application.id,
                    requiredAddon.id,
                ]),
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.locallyRemovedAddonIds.push(requiredAddon.id);
                    },
                    onFinish: () => {
                        this.removingRequiredAddonId = null;
                    },
                },
            );
        },
    },
}
</script>
